import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { ConsumedNote } from './ConsumedNote';
import { ProfilePrinterPanel } from './ProfilePrinterPanel';
import { PrintTabs } from './PrintTabs';
import {
  cancelJob,
  claimQueue,
  finishQueue,
  listTemplates,
  listQueue,
  printBlob,
  printQueue,
  type Consumed,
  type PrintJob,
  type SheetMode,
} from './api';

/**
 * The desk with the printer on it.
 *
 * The scenario this screen exists for: somebody walks the store with a phone and
 * orders labels for the boxes they are relabelling. Those cannot be printed
 * there — the big printer is on a desk over USB — so they wait here, and come out
 * as one document and one print dialog rather than seven.
 *
 * Nothing is marked printed by the act of printing. The browser hands the PDF to
 * a dialog and stops knowing anything; whether paper came out is a question only
 * the person standing there can answer, which is why they are asked.
 */
export function PrintStationPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<SheetMode>('labels');
  const [error, setError] = useState<string | null>(null);
  const [awaiting, setAwaiting] = useState<string[]>([]);
  // What the confirmed batch took off the roll. Kept on screen after the
  // question is answered, because "how many labels are left" is the thing the
  // person at the printer wanted to know all along.
  const [spent, setSpent] = useState<Consumed[]>([]);

  const queue = useQuery({
    queryKey: ['labels', 'queue'],
    queryFn: () => listQueue(),
    // The queue is filled from another device, so this screen has to look for
    // work rather than wait to be told about it.
    refetchInterval: 15_000,
  });
  // A direct Bluetooth job needs the physical roll dimensions as well as its
  // bitmap. Queue rows deliberately stay small, so look up the template here.
  const templates = useQuery({ queryKey: ['labels', 'templates', 'direct-print'], queryFn: () => listTemplates(undefined, undefined, true) });

  // Memoised because it is an effect dependency: `?? []` builds a new empty
  // array on every render, which would re-run the effect forever.
  const jobs = useMemo(() => queue.data ?? [], [queue.data]);

  // Everything is selected by default: the normal action here is "print what is
  // waiting", and having to tick seven boxes first would be busywork.
  useEffect(() => {
    setSelected((current) => {
      if (current.size > 0) return current;
      return new Set(jobs.filter((job) => job.status === 'queued').map((job) => job.id));
    });
  }, [jobs]);

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const print = useMutation({
    mutationFn: async () => {
      const ids = [...selected];
      const blob = await printQueue(ids, mode);
      printBlob(blob);
      return ids;
    },
    onSuccess: (ids) => {
      setError(null);
      setAwaiting(ids);
      void queryClient.invalidateQueries({ queryKey: ['labels', 'queue'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const confirm = useMutation({
    mutationFn: ({ ok }: { ok: boolean }) => finishQueue(awaiting, ok),
    onSuccess: (result) => {
      setAwaiting([]);
      setSelected(new Set());
      setSpent(result.consumed);
      void queryClient.invalidateQueries({ queryKey: ['labels', 'queue'] });
      // The roll is a part like any other, and its balance just moved.
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const drop = useMutation({
    mutationFn: (id: string) => cancelJob(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['labels', 'queue'] }),
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const selectedJobs = jobs.filter((job) => selected.has(job.id));

  return (
    <div className="station">
      <PrintTabs />
      <div className="station__head">
        <h2>{t('labels.station')}</h2>
        <span className="hint">{t('labels.queueCount', { count: jobs.length })}</span>
      </div>

      {error && <p className="error">{t(`labels.errors.${error}`, t('labels.errors.unknown'))}</p>}

      {spent.map((item, index) => (
        <ConsumedNote key={index} consumed={item} />
      ))}

      {awaiting.length > 0 && (
        <div className="station__confirm">
          <p>{t('labels.didItPrint', { count: awaiting.length })}</p>
          <button type="button" onClick={() => confirm.mutate({ ok: true })}>
            {t('labels.printedYes')}
          </button>
          {/* Back into the queue, not into a failed state nobody looks at: the
              roll ran out, and the label is still needed. */}
          <button type="button" onClick={() => confirm.mutate({ ok: false })}>
            {t('labels.printedNo')}
          </button>
        </div>
      )}

      {jobs.length === 0 && <p className="hint">{t('labels.queueEmpty')}</p>}

      {jobs.length > 0 && (
        <>
          <table className="station__table">
            <tbody>
              {jobs.map((job: PrintJob) => (
                <tr key={job.id} className={job.status === 'claimed' ? 'is-claimed' : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(job.id)}
                      onChange={() => toggle(job.id)}
                    />
                  </td>
                  <td>{job.title ?? job.entity_id}</td>
                  <td className="hint">×{job.copies}</td>
                  <td className="hint">{t(`labels.status.${job.status}`)}</td>
                  <td>
                    <button type="button" onClick={() => drop.mutate(job.id)}>
                      {t('common.cancel')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="station__actions">
            <label>
              {t('labels.mode')}
              <select value={mode} onChange={(event) => setMode(event.target.value as SheetMode)}>
                <option value="labels">{t('labels.modeLabels')}</option>
                <option value="sheet">{t('labels.modeSheet')}</option>
              </select>
            </label>
          </div>
          <ProfilePrinterPanel
            templateId="queue"
            items={selectedJobs.map((job) => {
              const template = templates.data?.find((item) => item.id === job.template_id);
              return { templateId: job.template_id, entityId: job.entity_id, copies: job.copies, medium: template ? { widthMm: template.media_width_mm, heightMm: template.media_height_mm } : undefined };
            })}
            systemBusy={print.isPending || selected.size === 0}
            onSystemPrint={() => print.mutate()}
            onDirectStart={async () => {
              const ids = selectedJobs.map((job) => job.id);
              await claimQueue(ids);
              setError(null);
              setAwaiting(ids);
              void queryClient.invalidateQueries({ queryKey: ['labels', 'queue'] });
            }}
            onDirectPrinted={async () => undefined}
          />
        </>
      )}
    </div>
  );
}
